import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { etapeSuivante } from 'store/etapes/etapesActions';
import {
    Button,
    Row,
    Col,
    LoaderMi,
    RadioControl,
    FieldsetControl,
    SegmentLoader,
} from '@si-armes/dsfr-react';
import InformationAdresse from 'components/adresse/InformationsAdresse';
import InformationsPersonnelles from 'components/informationsPersonnelles/InformationsPersonnelles';
import BoutonTelechargementFichier from 'components/BoutonTelechargementFichier/BoutonTelechargementFichier';
import { reinitialiseAPI, getAPI } from 'store/apiReducer';
import usagerClientReducer from 'store/usagerReducer';
import {
    API_RECUPERER_INFORMATIONS_PERSONNELLES,
    API_GET_DECLARATIONS_ARMES,
    CONTEXT_API_RECUPERER_INFORMATIONS_PERSONNELLES,
    CONTEXT_GET_DECLARATIONS_ARMES,
} from 'constants/apiUrls.constants';
import {
    METIER_CHOIX_CATEGORIE,
    METIERS_AUTO_CATEGORIE_C,
} from '../../../../../utils/Statuts.MetierAsso.constant';
import RoutesMesDemarches from '../../routes';
import { getPath } from '../../../../../utils/router';

const CreerTitreDetentionEtape1 = ({ isCategorieC, setIsCategorieC }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { isLoading, data, message, erreur } = useSelector(
        (state) => state.monCompte.recupererInformationsPersonnelles,
    );
    const hasPersonneMorale = data?.informationsMetiersAssociations?.hasPersonneMorale;
    const sousStatut = data?.informationsMetiersAssociations?.sousStatut;
    const isMineurNonEmancipe = data?.mineurEmancipe === false;
    const responsableLegal = data?.responsableLegal;

    // SIAR-6341 : seuls les Essais industriels choisissent la catégorie (radio).
    // Les métiers auto-flaggés Cat C (Nuisibles, Parcs zoologiques) n'ont pas de radio.
    const isChoixCategorie = sousStatut === METIER_CHOIX_CATEGORIE;

    // L'erreur de catégorie ne s'affiche qu'après une tentative de soumission du formulaire.
    const [tentativeSoumission, setTentativeSoumission] = useState(false);
    const categorieNonRenseignee = tentativeSoumission && isCategorieC === null;

    // SIAR-6341 : pour les métiers auto-flaggés Cat C, on initialise le flag à true.
    useEffect(() => {
        if (METIERS_AUTO_CATEGORIE_C.includes(sousStatut) && isCategorieC !== true) {
            setIsCategorieC(true);
        }
    }, [sousStatut, isCategorieC, setIsCategorieC]);

    // SIAR-6341 : récupérer les catégories autorisées depuis l'API
    useEffect(() => {
        if (!isChoixCategorie) {
            return;
        }
        dispatch(
            getAPI(usagerClientReducer, API_GET_DECLARATIONS_ARMES, CONTEXT_GET_DECLARATIONS_ARMES),
        );
    }, [isChoixCategorie, dispatch]);

    // SIAR-6341 : extraire les catégories autorisées du store Redux
    const { data: declarationsData, isLoading: isLoadingDeclarations } = useSelector(
        (state) => state.mesDeclarationsArmes.declarationsArmes,
    );

    // SIAR-6341 : catégories autorisées pour les Essais industriels
    const categoriesAutorisees = declarationsData?.demanderMultipleAutorisations || [];

    // SIAR-6341 : définir la sélection par défaut selon les catégories autorisées
    useEffect(() => {
        if (isChoixCategorie && isCategorieC === null && categoriesAutorisees.length > 0) {
            if (categoriesAutorisees.includes('AB') && !categoriesAutorisees.includes('C')) {
                setIsCategorieC(false);
            } else if (categoriesAutorisees.includes('C') && !categoriesAutorisees.includes('AB')) {
                setIsCategorieC(true);
            }
        }
    }, [categoriesAutorisees, isChoixCategorie, isCategorieC, setIsCategorieC]);

    const recuperationInformations = () => {
        dispatch(
            getAPI(
                usagerClientReducer,
                API_RECUPERER_INFORMATIONS_PERSONNELLES,
                CONTEXT_API_RECUPERER_INFORMATIONS_PERSONNELLES,
            ),
        );
        return () => {
            dispatch(reinitialiseAPI(CONTEXT_API_RECUPERER_INFORMATIONS_PERSONNELLES));
        };
    };

    useEffect(() => {
        recuperationInformations();
    }, []);

    if (isLoading) {
        return (
            <div className="loader-full-screen">
                <LoaderMi taille="md" />
            </div>
        );
    }
    if (erreur) {
        return (
            <div className="margin-top-md margin-bottom-md">
                <p>{message}</p>
            </div>
        );
    }

    if (isLoading === false && !data.personnePhysique) {
        return (
            <div className="margin-top-md margin-bottom-md">
                <p>Une erreur s&apos;est produite !</p>
            </div>
        );
    }

    let fichier = null;
    if (isMineurNonEmancipe && responsableLegal?.adressePrincipale?.justificatifsDomicile) {
        [fichier] = responsableLegal.adressePrincipale.justificatifsDomicile.fichiers;
    } else if (data?.personnePhysique?.adressePrincipale?.justificatifsDomicile) {
        [fichier] = data.personnePhysique.adressePrincipale.justificatifsDomicile.fichiers;
    }

    return (
        <SegmentLoader isLoading={isLoadingDeclarations}>
            <div>
                {/* SIAR-6341 : choix de la catégorie pour les Essais industriels */}
                {isChoixCategorie && (
                    <Row gutters className="fr-grid-row--top margin-bottom-md">
                        <Col lg={12}>
                            <FieldsetControl
                                id="choixCategorieDemande"
                                legend="Catégorie d'armes concernée par la demande d'autorisation *"
                                state={categorieNonRenseignee ? 'error' : 'default'}
                                message={
                                    categorieNonRenseignee
                                        ? 'Veuillez sélectionner une catégorie'
                                        : null
                                }
                                inline
                            >
                                <RadioControl
                                    className="margin-right-sm"
                                    id="choixCategorieDemande-ab"
                                    name="choixCategorieDemande"
                                    label="Catégorie A/B"
                                    value={false}
                                    checked={isCategorieC === false}
                                    onClick={() => {
                                        if (
                                            categoriesAutorisees.includes('AB') ||
                                            categoriesAutorisees.includes('C')
                                        ) {
                                            setIsCategorieC(false);
                                        }
                                    }}
                                    disabled={
                                        categoriesAutorisees.length > 0 &&
                                        !categoriesAutorisees.includes('AB')
                                    }
                                />
                                <RadioControl
                                    className="margin-right-sm"
                                    id="choixCategorieDemande-c"
                                    name="choixCategorieDemande"
                                    label="Catégorie C"
                                    value
                                    checked={isCategorieC === true}
                                    onClick={() => {
                                        if (
                                            categoriesAutorisees.includes('AB') ||
                                            categoriesAutorisees.includes('C')
                                        ) {
                                            setIsCategorieC(true);
                                        }
                                    }}
                                    disabled={
                                        categoriesAutorisees.length > 0 &&
                                        !categoriesAutorisees.includes('C')
                                    }
                                />
                            </FieldsetControl>
                        </Col>
                    </Row>
                )}
                <Row gutters className="fr-grid-row--top">
                    {isMineurNonEmancipe && responsableLegal ? (
                        <>
                            <Col lg={6}>
                                <h3 className="margin-top-xs font-size-lg">
                                    Identité du responsable légal
                                </h3>
                                <InformationsPersonnelles
                                    dataInformationsPersonnelles={responsableLegal}
                                />
                            </Col>
                            <Col lg={6}>
                                <h3 className="margin-top-xs font-size-lg">
                                    Identité du détenteur mineur
                                </h3>
                                {data?.personnePhysique && (
                                    <InformationsPersonnelles
                                        dataInformationsPersonnelles={data.personnePhysique}
                                    />
                                )}
                            </Col>
                        </>
                    ) : (
                        <>
                            <Col lg={6}>
                                <h3 className="margin-top-xs font-size-lg">
                                    {hasPersonneMorale
                                        ? 'Informations du représentant légal'
                                        : 'Informations du détenteur'}
                                </h3>
                                {data?.personnePhysique && (
                                    <InformationsPersonnelles
                                        dataInformationsPersonnelles={data.personnePhysique}
                                    />
                                )}
                            </Col>

                            <Col lg={6}>
                                <h3 className="margin-top-xs font-size-lg">
                                    {hasPersonneMorale
                                        ? 'Adresse de résidence principale du responsable légal'
                                        : 'Adresse de résidence principale'}
                                </h3>
                                {data?.personnePhysique?.adressePrincipale && (
                                    <InformationAdresse
                                        adresse={data.personnePhysique.adressePrincipale}
                                        afficherBoutonAction={false}
                                    />
                                )}

                                <div className="margin-top-md">
                                    {fichier && (
                                        <BoutonTelechargementFichier
                                            libelle={fichier.filename}
                                            uuidFichier={fichier.uuid}
                                            key={fichier.uuid}
                                            aria-label="Télécharger le justificatif de domicile"
                                        />
                                    )}
                                </div>
                            </Col>
                        </>
                    )}
                </Row>

                {isMineurNonEmancipe && responsableLegal?.adressePrincipale && (
                    <Row gutters className="fr-grid-row--top">
                        <Col lg={6}>
                            <h3 className="margin-top-xs font-size-lg">Résidence principale</h3>
                            <InformationAdresse
                                adresse={responsableLegal.adressePrincipale}
                                afficherBoutonAction={false}
                            />
                            <div className="margin-top-md">
                                {fichier && (
                                    <BoutonTelechargementFichier
                                        libelle={fichier.filename}
                                        uuidFichier={fichier.uuid}
                                        key={fichier.uuid}
                                        aria-label="Télécharger le justificatif de domicile"
                                    />
                                )}
                            </div>
                        </Col>
                    </Row>
                )}

                <Row gutters className="margin-top-md">
                    <Col lg={12}>
                        <i>
                            Si vous constatez des erreurs concernant les informations sur votre
                            identité ou votre adresse de résidence principale, merci de les modifier
                            depuis votre gestion de compte en cliquant sur le bouton "Mon profil"
                        </i>
                    </Col>
                </Row>
                <Row gutters className="margin-top-md">
                    <Col lg={6}>
                        <Button
                            name="Annuler"
                            onClick={() => history.push(getPath(RoutesMesDemarches))}
                            variant="secondary"
                        >
                            Annuler
                        </Button>
                    </Col>
                    <Col lg={6}>
                        <div className="text-right">
                            <Button
                                name="EtapeSuivante"
                                variant="primary"
                                onClick={() => {
                                    if (isChoixCategorie && isCategorieC === null) {
                                        setTentativeSoumission(true);
                                        return;
                                    }
                                    dispatch(etapeSuivante());
                                }}
                            >
                                Étape Suivante
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>
        </SegmentLoader>
    );
};

export default CreerTitreDetentionEtape1;

CreerTitreDetentionEtape1.propTypes = {
    isCategorieC: PropTypes.bool,
    setIsCategorieC: PropTypes.func,
};
